import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import IndeterminateCheckBoxOutlinedIcon from "@mui/icons-material/IndeterminateCheckBoxOutlined";
import { Box, CircularProgress } from "@mui/material";
import {
  Dispatch,
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import TreeView from "react-accessible-treeview";

import { BORDER_RADIUS } from "@/constants";
import CheckBoxOutlineBlankOutlinedIcon from "@mui/icons-material/CheckBoxOutlineBlankOutlined";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import "./styles.css";

/**TODO: Add checkbox to parent values*/
/**FIXME: tree state requires all selected child ids but our controlled state only has ids of leaf nodes*/

export default function CustomAsyncTree<TreeData, Type>({
  handleLoadData,
  setSelected,
  selected,
  nodes,
  setNodes,
  setType,
  removeType,
  data,
  setData,
  setAllNodes,
  setMultipleType,
  removeMultipleType,
}: CustomAsyncTreeProps<TreeData, Type>) {
  const loadedAlertElement = useRef(null);

  const [localeSelected, setLocaleSelected] = useState<string[]>([]);

  useEffect(() => {
    if (!data) setNodes({});
    else {
      const reducedObject = data?.reduce(
        (acc: Record<string, any>, val: Record<string, any>) => {
          if (acc && (!acc[val.id] || !acc[val.id]?.parent_key)) {
            acc[val.id] = val;
          }
          return acc;
        },
        {}
      );
      setNodes(reducedObject);
    }
  }, [data]);
  useEffect(() => {
    if (Object.keys(nodes)?.length)
      setAllNodes((current: any) => ({ ...current, ...nodes }));
  }, [nodes]);

  const filteredSelectedValues = useMemo(() => {
    return selected.filter((itemId: string) => nodes[itemId]);
  }, [nodes, JSON.stringify(selected)]);

  useEffect(() => {
    if (!selected?.length) {
      setLocaleSelected([]);
      return;
    }
    setLocaleSelected(filteredSelectedValues);
  }, [JSON.stringify(nodes), JSON.stringify(selected)]);

  const updateTreeData = (list: any, id: any, children: any) => {
    const listData = list.map((node: any) => {
      if (node.id === id) {
        const mySet = new Set(node.children);
        children.forEach((el: any) => {
          mySet.add(el.id);
        });
        node.children = Array.from(mySet);
      }
      return node;
    });

    const filterChildren = children.filter((item: any) =>
      nodes?.[item.id] ? false : true
    );

    const concatedData = listData.concat(filterChildren);

    return concatedData;
  };

  const onLoadData = async ({ element }: any) => {
    const response: any = await handleLoadData({
      accountId: element?.accountId,
      dataProviderId: element?.dataProviderId,
      key: element?.id,
    });

    const mappedData = response?.results?.map((item: any) => {
      const isChildren = Boolean(item.children_url);
      return {
        name: item.name,
        id: item.key,
        children: [],
        isChildren,
        isBranch: isChildren,
        parent: element.id,
        dataProviderId: item.data_provider_id,
        ...(item.verb_usage ? { verb_usage: item.verb_usage } : {}),
      };
    });

    setData((value: any) => updateTreeData(value, element.id, mappedData));

    return;
  };

  const wrappedOnLoadData = async (props: any) => {
    await onLoadData(props);
  };

  return (
    <>
      <Box sx={{ display: "block" }}>
        <div
          className="visually-hidden"
          ref={loadedAlertElement}
          role="alert"
          aria-live="polite"
        ></div>
        <Box
          className="checkbox"
          sx={{
            minHeight: 0,
            maxHeight: "100%",
            height: "100%",
            overflow: "auto",
            backgroundColor: "background.paper",
            borderRadius: BORDER_RADIUS,
          }}
        >
          <TreeView
            data={data as any[]}
            aria-label="Checkbox tree"
            onLoadData={wrappedOnLoadData}
            multiSelect
            propagateSelect
            togglableSelect
            propagateSelectUpwards
            selectedIds={localeSelected}
            onNodeSelect={(value) =>
              setIds<TreeData, Type>({
                value,
                selected,
                setSelected,
                setType,
                removeType,
                nodes,
                removeMultipleType,
                setMultipleType,
              })
            }
            nodeRenderer={({
              element,
              isBranch,
              isExpanded,
              isSelected,
              isHalfSelected,
              getNodeProps,
              level,
              handleSelect,
              handleExpand,
            }) => {
              const branchNode = (isExpanded: any, element: any) => {
                return isExpanded && element.children.length === 0 ? (
                  <>
                    <span
                      role="alert"
                      aria-live="assertive"
                      className="visually-hidden"
                    >
                      loading {element.name}
                    </span>
                    <Box
                      sx={{ paddingRight: "2px", display: "inline-block" }}
                    >
                      <CircularProgress
                        aria-hidden={true}
                        className="loading-icon"
                        size="15px"
                        sx={{ verticalAlign: "middle" }}
                      />
                    </Box>
                  </>
                ) : (
                  <ArrowIcon isOpen={isExpanded} />
                );
              };
              return (
                <div
                  {...getNodeProps({ onClick: handleExpand })}
                  style={{ marginLeft: 40 * (level - 1) }}
                >
                  {isBranch && branchNode(isExpanded, element)}
                  {!isBranch && (
                    <CheckBoxIcon
                      className="checkbox-icon"
                      onClick={(e) => {
                        handleSelect(e);
                        e.stopPropagation();
                      }}
                      variant={
                        isHalfSelected ? "some" : isSelected ? "all" : "none"
                      }
                    />
                  )}
                  <span className="name">{element.name}</span>
                </div>
              );
            }}
          />
        </Box>
      </Box>
    </>
  );
}

interface CustomAsyncTreeProps<TreeData, Type> {
  handleLoadData: (args: {
    key: string;
    dataProviderId: number;
    accountId: number;
  }) => void;
  setSelected: (nodes: string[]) => void;
  selected: string[];
  nodes: Record<string, any>;
  setNodes: Dispatch<React.SetStateAction<Record<string, any>>>;
  setType: (verbUsage: Type, id: string) => void;
  removeType: (id: string) => void;
  data: Record<string, any>[];
  setData: Dispatch<React.SetStateAction<TreeData[]>>;
  setAllNodes: Dispatch<React.SetStateAction<Record<string, any>>>;
  setMultipleType: (ids: string[]) => void;
  removeMultipleType: (ids: string[]) => void;
}

type SetIds<TreeData, Type> = Pick<
  CustomAsyncTreeProps<TreeData, Type>,
  | "selected"
  | "setSelected"
  | "setType"
  | "nodes"
  | "setMultipleType"
  | "removeMultipleType"
  | "removeType"
> & { value: any };

const ArrowIcon = ({ isOpen }: { isOpen: boolean }) => {
  const Icon = isOpen ? ArrowDropDownIcon : ArrowRightIcon;
  return <Icon sx={{ verticalAlign: "middle" }} />;
};

const CheckBoxIcon = ({
  variant,
  ...rest
}: {
  variant: "all" | "none" | "some";
  className: string;
  onClick: (e: MouseEvent<unknown, unknown> | KeyboardEvent<unknown>) => void;
}) => {
  switch (variant) {
    case "all":
      return <CheckBoxOutlinedIcon {...rest} />;
    case "none":
      return <CheckBoxOutlineBlankOutlinedIcon {...rest} />;
    case "some":
      return <IndeterminateCheckBoxOutlinedIcon {...rest} />;
    default:
      return null;
  }
};

function setIds<TreeData, Type>({
  selected,
  setSelected,
  value,
  setType,
  removeType,
  nodes,
  removeMultipleType,
  setMultipleType,
}: SetIds<TreeData, Type>) {
  if (!value) return;

  if (!value.element?.isChildren) {
    const selectedItem = selected.find(
      (item: any) => item === value?.element.id
    );
    // is leaf node
    if (!value.isSelected && selectedItem) {
      const newSelectedValues = selected.filter(
        (item: any) => item !== selectedItem
      );

      setSelected(newSelectedValues);
      removeType(value?.element.id);
      return;
    }
    if (selectedItem) return;

    const newSelectedValues = Array.from(
      new Set([...selected, value.element.id])
    );

    setSelected(newSelectedValues);
    setType(value?.element.verb_usage, value.element.id);
  } else {
    // get leaves
    const leafNodesToAdd = recursiveTraversal({
      nodes,
      element: nodes[value?.element.id],
    });

    if (!value.isSelected || value.isHalfSelected === false) {
      // remove from selected
      const newSelectedValues = selected.filter(
        (item: any) => !leafNodesToAdd.includes(item)
      );

      setSelected(newSelectedValues);
      removeMultipleType(leafNodesToAdd);
      // loop and remove type
      return;
    } else if (value.isSelected || value.isHalfSelected === true) {
      // add to selected
      const newSelectedValues = Array.from(
        new Set([...selected, ...leafNodesToAdd])
      );

      setSelected(newSelectedValues);
      setMultipleType(leafNodesToAdd);
    }
  }
}

function recursiveTraversal({
  nodes,
  element,
}: {
  nodes: Record<string, any>;
  element: any;
}): string[] {
  if (!element) return [];
  const { id, isChildren: hasChildren, children } = element;

  if (!hasChildren) return id;
  else {
    return children.flatMap((childId: string) => {
      return recursiveTraversal({
        nodes,
        element: nodes[childId],
      });
    });
  }
}

function anyChildrenIsAValue(children: any, list: any) {
  return children.some((childKey: any) => {
    const child = list.find((item: any) => item.id === childKey);
    return !child.isChildren;
  });
}
